/**
 * Pure helpers shared between the team-audit full page
 * (`/teams/:teamId/audit`) and the team-audit ticker section in the
 * persistent right panel (IA-rework PR 2, #196).
 *
 * Extracted from `web/src/routes/TeamAuditPage.tsx` so the panel-side
 * ticker can render the same plain-English action labels without
 * duplicating the action-to-summary table. The full page renders these
 * inside a 640px column with role chips; the ticker renders a slimmer
 * row inside a 330px panel — only the formatters / summariser are
 * shared, the JSX shape stays separate per
 * `feedback_ui_section_focal_component.md` (full page = list-as-focal,
 * ticker = compact supporting context).
 */
import type { components } from "../api/schema.gen";

type AuditEventResponse = components["schemas"]["AuditEventResponse"];

export interface SummaryParts {
  /** One-line plain-English description. */
  label: string;
  /** Optional secondary detail derived from `payload`. */
  detail?: string;
}

/**
 * Plain-English row summaries for every action the lead's team feed can
 * realistically produce. Mirrors `/me/audit`'s `summarise()` shape but
 * framed for the team-scoped audience: "A member submitted their
 * availability" rather than "You submitted your availability". Anything
 * unmapped falls through to a humanised version of the action string.
 *
 * Keep these calm + factual. Trustable per brand pillar means a lead
 * reading the row should feel they could share it with the member
 * involved without tonal whiplash.
 */
export function summariseTeamAuditEvent(event: AuditEventResponse): SummaryParts {
  const payload = (event.payload ?? {}) as Record<string, unknown>;
  switch (event.action) {
    case "submission.created":
      return withOptionalDetail(
        "A member submitted their availability",
        detailFromLeaveCount(payload),
      );
    case "submission.bulk_blocks_write":
      return { label: "A member updated their availability blocks" };
    case "submission.lead_bypass_write":
      return withOptionalDetail("Lead edited a member's submission", detailFromLeaveCount(payload));
    case "member.patched":
      return withOptionalDetail(
        "A team member's profile was updated",
        detailFromMemberPatch(payload),
      );
    case "leave.dispute_filed":
      return { label: "A member raised a dispute on a leave decision" };
    case "leave.decision_recorded":
      return { label: "A leave decision was recorded" };
    case "invite.accepted":
      return { label: "A new member joined the team" };
    case "invite.created":
      return { label: "An invite was sent" };
    case "invite.revoked":
      return { label: "An invite was revoked" };
    case "bid.placed":
      return { label: "A bid was placed" };
    case "member_message.sent":
      return { label: "A member sent the lead a message" };
    case "member_message.read":
      return { label: "A message from a member was marked read" };
    case "schedule_republished":
      return { label: "The team's roster was re-published" };
    case "cycle.published":
      return { label: "A roster was published" };
    case "cycle.opened":
      return { label: "Submissions opened" };
    case "cycle.closed":
      return { label: "Submissions closed" };
    case "cycle.solve.started":
      return { label: "Scheduler run started" };
    case "cycle.solve.completed":
      return { label: "Scheduler run completed" };
    case "cycle.solve.continue_from_last":
      // #3002 S6 — the console's Last solve still writes this action (S5).
      return { label: "Solve continued from the last solve" };
    case "cycle.solve.continue_from":
      // #3002 S6 — a Continue from the current roster or a saved version.
      return { label: `Solve continued from ${continueFromWhat(payload)}` };
    case "team.config.updated":
      return { label: "Team configuration was updated" };
    default:
      return { label: humaniseAction(event.action) };
  }
}

/** Helper so the `detail` key only appears when defined — keeps the
 *  return shape compatible with `exactOptionalPropertyTypes: true`. */
function withOptionalDetail(label: string, detail: string | undefined): SummaryParts {
  return detail !== undefined ? { label, detail } : { label };
}

/** Where a Continue started, from the `cycle.solve.continue_from` payload:
 *  "the current roster", the version's label ("Solve 1", "Final"), or "a
 *  saved version" when the row names none. */
function continueFromWhat(payload: Record<string, unknown>): string {
  if (payload.source === "current") return "the current roster";
  if (payload.source === "last_solve") return "the last solve";
  const label = payload.version_label;
  return typeof label === "string" && label.trim() ? label.trim() : "a saved version";
}

export function humaniseAction(action: string): string {
  // "team.config.station.created" → "Team config station created"
  const parts = action.replace(/[._]/g, " ").split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return action;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...parts.slice(1)].join(" ");
}

function detailFromLeaveCount(payload: Record<string, unknown>): string | undefined {
  const leaveDates = payload.leave_dates;
  if (Array.isArray(leaveDates)) {
    const n = leaveDates.length;
    if (n === 0) return "No days off requested.";
    return `${n} day${n === 1 ? "" : "s"} off.`;
  }
  return undefined;
}

function detailFromMemberPatch(payload: Record<string, unknown>): string | undefined {
  const candidate =
    (payload.changes as Record<string, unknown> | undefined) ??
    (payload.after as Record<string, unknown> | undefined);
  if (candidate && typeof candidate === "object") {
    const keys = Object.keys(candidate);
    if (keys.length > 0) {
      return `Fields changed: ${keys.join(", ")}.`;
    }
  }
  return undefined;
}

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const diffMs = now.getTime() - ts;
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} month${diffMonth === 1 ? "" : "s"} ago`;
  const diffYear = Math.round(diffMonth / 12);
  return `${diffYear} year${diffYear === 1 ? "" : "s"} ago`;
}

export function formatAbsolute(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}
